import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { AdvancedSearch } from "@/components/AdvancedSearch";
import { AuthMenu } from "@/components/AuthMenu";
import { ClassicSearch } from "@/components/ClassicSearch";
import { SearchModeToggle } from "@/components/SearchModeToggle";
import { TileGrid } from "@/components/TileGrid";
import { useCurrentUser } from "@/hooks/useAuth";
import { useSearch } from "@/hooks/useSearch";
import {
  useAddSavedSearch,
  useRemoveSavedSearch,
  useSavedSearches,
} from "@/hooks/useUserData";
import { usePreferences } from "@/store/preferences";
import { trackSearch } from "@/analytics/analytics";

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const trackedQuery = useRef<string | null>(null);
  const {
    savedSearches: localSaved,
    saveSearch: saveLocal,
    removeSavedSearch: removeLocal,
    searchMode,
  } = usePreferences();
  const { data: user } = useCurrentUser();
  const { data: serverSaved = [] } = useSavedSearches();
  const { mutate: addServer } = useAddSavedSearch();
  const { mutate: removeServer } = useRemoveSavedSearch();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearch(query);

  const posts = data?.pages.flatMap((p) => p.posts) ?? [];
  const hasMore = !!hasNextPage;
  // Used only for the "X results" label; no longer the true global count.
  // We dropped the COUNT(*) at the API to keep multi-tag search fast on
  // the image-widened table.
  const loadedCount = posts.length;

  useEffect(() => {
    if (query && trackedQuery.current !== query && !isLoading) {
      trackedQuery.current = query;
      const tags = query.split(/\s+/).filter((t) => !t.startsWith("order:"));
      trackSearch({ tags, result_count: loadedCount });
    }
  }, [query, isLoading, loadedCount]);

  // Saved searches go to the server when signed in, localStorage when anonymous.
  // This lets users sample the feature before registering and still keeps their
  // pre-login bookmarks visible while signed-out.
  const isSaved = user
    ? serverSaved.some((s) => s.query === query)
    : localSaved.some((s) => s.query === query);

  function toggleSaved() {
    if (user) {
      if (isSaved) removeServer(query);
      else addServer({ query });
    } else if (isSaved) {
      removeLocal(query);
    } else {
      saveLocal(query);
    }
  }

  function handleSearch(q: string) {
    setSearchParams(q ? { q } : {});
  }

  return (
    <>
      <Helmet>
        <title>{query ? `${query} — GIF Search` : "GIF Search"}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-6xl mx-auto space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <SearchModeToggle />
              {query && (
                <button
                  onClick={toggleSaved}
                  className="shrink-0 text-sm text-zinc-500 hover:text-accent-light transition-colors min-h-[40px] px-2 inline-flex items-center"
                  title={
                    isSaved
                      ? "Remove saved search"
                      : user
                        ? "Save (synced to your account)"
                        : "Save (locally, sign in to sync)"
                  }
                >
                  {isSaved ? "★ saved" : "☆ save"}
                </button>
              )}
              <div className="ml-auto">
                <AuthMenu />
              </div>
            </div>

            {searchMode === "classic" ? (
              <ClassicSearch initialQuery={query} onSearch={handleSearch} />
            ) : (
              <AdvancedSearch initialQuery={query} onSearch={handleSearch} />
            )}
          </div>
        </div>

        {/* Result count */}
        {!isLoading && query && (
          <div className="px-4 py-2 max-w-6xl mx-auto w-full">
            <p className="text-xs text-zinc-600">
              {loadedCount.toLocaleString()}
              {hasMore ? "+" : ""} result{loadedCount === 1 ? "" : "s"}
            </p>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 px-4 pb-8 max-w-6xl mx-auto w-full">
          <TileGrid
            posts={posts}
            isLoading={isLoading}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
    </>
  );
}
